import { CorpusId } from '../../models/aliases';
import { events } from '../../analytics/events';
import { useTrackedHandler } from '../../hooks/useTracking';

import {
  Button,
  ClickAwayListener,
  Grid,
  IconButton,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEventHandler, MouseEventHandler, useState } from 'react';
import CloseIcon from '@mui/icons-material/Cancel';
import styled from 'styled-components';

type FeedbackPopoverProps = {
  corpusId: CorpusId;
  onSubmitFeedback: (feedback: string) => Promise<void>;
  closeFeedbackForm: () => void;
  isFeedbackFormOpen: boolean;
  feedbackFormAnchorEl: Element | null;
  messageTemplateText?: string;
  instructionsText?: string;
};

export function FeedbackPopover(props: FeedbackPopoverProps) {
  const {
    corpusId,
    onSubmitFeedback,
    closeFeedbackForm,
    isFeedbackFormOpen = false,
    feedbackFormAnchorEl = null,
    messageTemplateText = '',
    instructionsText,
  } = props;

  const [feedbackFormState, setFeedbackFormState] = useState({
    text: messageTemplateText,
    isSaving: false,
    errorMessage: '',
  });

  const onChangeFeedbackText: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    setFeedbackFormState({
      ...feedbackFormState,
      text: event.target.value,
    });
  };

  const submitFeedback = async () => {
    const feedback = feedbackFormState.text.trim();
    if (!feedback) {
      // Don't submit empty feedback
      return;
    }

    setFeedbackFormState({
      ...feedbackFormState,
      isSaving: true,
      errorMessage: '',
    });

    await Promise.all([
      onSubmitFeedback(feedback),
      new Promise((resolve) => setTimeout(resolve, 1000)),
    ]);

    // Clear form if successfully submitted
    setFeedbackFormState({
      ...feedbackFormState,
      isSaving: false,
      text: '',
      errorMessage: '',
    });
  };

  const onClickSubmitFeedback: MouseEventHandler = (): void => {
    submitFeedback().catch(async (error) => {
      const errorMessage = 'Error: Failed to save feedback';
      console.error(errorMessage, error);
      setFeedbackFormState({
        ...feedbackFormState,
        isSaving: false,
        errorMessage,
      });
    });
  };

  const reportFeedbackInputFocus = useTrackedHandler(
    events.feedback.TextFeedbackFocused,
    (): void => {},
    () => ({ corpusId }),
  );

  return (
    <StyledPopper
      open={isFeedbackFormOpen}
      anchorEl={feedbackFormAnchorEl}
      placement="bottom"
      disablePortal={false}
      modifiers={[
        {
          name: 'flip',
          enabled: true,
          options: {
            altBoundary: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
      ]}
      sx={{
        width: '95%',
        maxWidth: 480,
        zIndex: 1,
      }}
    >
      <ClickAwayListener onClickAway={closeFeedbackForm}>
        <Paper
          sx={{
            p: 2,
            width: '100%',
            backgroundColor: 'rgb(8, 35, 43)',
          }}
          elevation={3}
        >
          <FeedbackHeader>
            <Typography variant="h6">Leave Additional Feedback</Typography>

            <IconButton onClick={closeFeedbackForm}>
              <CloseIcon fontSize="small" color="secondary" />
            </IconButton>
          </FeedbackHeader>
          {instructionsText && (
            <Typography variant="subtitle2" color="text.reversed">
              {instructionsText}
            </Typography>
          )}
          <FeedbackInput
            fullWidth
            multiline
            placeholder="Feedback"
            disabled={feedbackFormState.isSaving}
            value={feedbackFormState.text}
            onChange={onChangeFeedbackText}
            onFocus={reportFeedbackInputFocus}
            margin="normal"
            size="small"
            minRows={3}
            maxRows={5}
          />

          <Grid item xs={12} textAlign="right">
            {feedbackFormState.errorMessage && (
              <Typography
                align="left"
                color="error"
                data-testid="feedback-error"
                variant="body2"
              >
                Error submitting feedback, please try again.
              </Typography>
            )}
            <SubmitFeedbackButton
              disabled={feedbackFormState.isSaving}
              onClick={onClickSubmitFeedback}
              color="secondary"
            >
              Submit
            </SubmitFeedbackButton>
          </Grid>
        </Paper>
      </ClickAwayListener>
    </StyledPopper>
  );
}

// NOTE: The popper z-index must be above the z-index of the widget drawer in NORA
//       or this popper will appear under the papers div (i.e. won't show)
const StyledPopper = styled(Popper)`
  &.MuiPopper-root {
    z-index: 1200;
  }
`;

const SubmitFeedbackButton = styled(Button).attrs({
  variant: 'contained',
  size: 'small',
})`
  &.MuiButtonBase-root {
    color: rgb(10, 50, 53);
  }

  &.MuiButtonBase-root:hover {
    color: rgb(10, 50, 53);
  }
`;

const FeedbackInput = styled(TextField)`
  &.MuiFormControl-root {
    width: 100%;
    margin-bottom: 12px;
    margin-top: 6px;
    .MuiInputBase-root {
      border: 1px solid rgba(255, 240, 243, 0.6);
      border-radius: 4px;
      .MuiInputBase-input {
        color: ${(props) => props.theme.color['cream-100'].hex};
      }
    }
  }
`;

const FeedbackHeader = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;

  h6 {
    color: ${(props) => props.theme.color['cream-100'].hex};
  }

  button {
    padding: 0;
  }
`;
